import React from "react";
import { Link } from "react-router-dom";
import { Scale, ArrowLeft } from "lucide-react";
import { LandingFooter } from "@/components/landing/CtaBanner";

// The only contact address currently configured for the service (ADMIN_EMAIL).
const CONTACT_EMAIL = "supravat111@gmail.com";
const EFFECTIVE_DATE = "12 September 2026";

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`policy-section-${id}`} className="scroll-mt-24">
    <h2 className="font-display text-2xl font-semibold tracking-tight text-navy sm:text-[1.7rem]">{title}</h2>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-[0.95rem]">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="ml-1 space-y-2.5">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3">
        <span aria-hidden="true" className="mt-[0.5rem] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const TOC = [
  ["scope", "1. About this policy"],
  ["collect", "2. Information we collect"],
  ["content", "3. Information you enter into the service"],
  ["documents", "4. Documents you upload"],
  ["portal", "5. Client portal and invitations"],
  ["notifications", "6. Notifications and communication"],
  ["payments", "7. Payment and subscription information"],
  ["security-info", "8. Password and security information"],
  ["use", "9. How we use information"],
  ["isolation", "10. Organisation-level access isolation"],
  ["third-parties", "11. Third-party services we use"],
  ["your-security", "12. Your account security responsibilities"],
  ["deletion", "13. Data deletion and account closure"],
  ["requests", "14. Privacy requests and contact"],
  ["updates", "15. Changes to this policy"],
  ["law", "16. Governing law"],
];

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page" className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="policy-brand" className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-navy text-gold">
              <Scale className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <span className="font-display text-xl tracking-tight text-navy">Advocate Diary</span>
          </Link>
          <Link
            to="/"
            data-testid="policy-back-home"
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy/70 transition-colors hover:bg-secondary hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
        </div>
      </header>

      <div className="border-b border-border bg-navy px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold">Legal</p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            How Advocate Diary handles the information you and your firm put into the service.
          </p>
          <p className="mt-6 text-xs text-slate-400" data-testid="policy-effective-date">
            Effective date: {EFFECTIVE_DATE}
          </p>
        </div>
      </div>

      <main className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[220px_minmax(0,1fr)]">
          <nav data-testid="policy-toc" className="hidden lg:block">
            <div className="sticky top-24">
              <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
                On this page
              </p>
              <ul className="mt-4 space-y-2 border-l border-border pl-4">
                {TOC.map(([id, label]) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      data-testid={`policy-toc-${id}`}
                      className="block text-[13px] leading-snug text-muted-foreground transition-colors hover:text-navy"
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          <article className="max-w-3xl space-y-12">
            <Section id="scope" title="1. About this policy">
              <p>
                Advocate Diary is a practice management application for advocates, chambers and law firms in
                India. It is a software service only. Advocate Diary is not a law firm, does not act as an
                advocate or legal advisor for any person, and does not provide legal advice or legal opinions.
                Nothing in the service or in this policy creates an advocate-client relationship.
              </p>
              <p>
                This policy explains what information the application collects, how it is used, and the controls
                that apply to it. It describes only practices that are actually implemented in the application as
                it operates today.
              </p>
              <p>
                Where a firm uses Advocate Diary to record information about its own clients and matters, the
                firm decides what information to enter and remains responsible for it, including for its
                professional and confidentiality obligations towards its clients. Advocate Diary processes that
                information in order to provide the service to the firm.
              </p>
            </Section>

            <Section id="collect" title="2. Information we collect">
              <p>We collect the following when you register and use an account:</p>
              <Bullets
                items={[
                  "Registration details: your name, email address, mobile number, the account type you choose (individual advocate or law firm), and the firm name where you provide one.",
                  "Profile details you maintain in Settings, such as your name and mobile number.",
                  "Organisation details for your practice, including the practice or firm name and the practice details you choose to save in Settings.",
                  "Team information where a firm administrator adds advocates, staff or client portal users, including the name, email address and role assigned to each member.",
                  "Sign-in information, including the time of your last successful sign-in, and records of sign-in sessions so you can stay signed in.",
                  "If you sign in with Google, the name, email address and profile picture returned by that sign-in.",
                ]}
              />
              <p>
                The application does not use cookies or similar technologies for advertising, and it does not
                include advertising trackers.
              </p>
            </Section>

            <Section id="content" title="3. Information you enter into the service">
              <p>
                The substance of what Advocate Diary holds is the information you enter. This typically includes:
              </p>
              <Bullets
                items={[
                  "Client records, including the names and contact details you choose to store for your clients.",
                  "Case and matter records, including case numbers, courts, parties, case type and status, and the notes you add.",
                  "Hearing records, including dates, courts, benches, court rooms, outcomes and adjournments.",
                  "Tasks, reminders and diary entries.",
                  "Court details you add for your own use.",
                  "A timeline of activity on your matters, and updates you choose to publish to a client.",
                ]}
              />
              <p>
                This information is entered at your discretion. You decide what is recorded about a matter or a
                client, and you can edit or remove records from within the application.
              </p>
            </Section>

            <Section id="documents" title="4. Documents you upload">
              <p>
                You can upload documents and attach them to your matters. Uploaded files are stored using
                Cloudflare R2 object storage, which the application uses as its document store.
              </p>
              <Bullets
                items={[
                  "Documents are not served from public links. Each download is authorised against your signed-in account before access is given.",
                  "Access to a document is limited to users of the organisation the document belongs to.",
                  "Alongside the file, the application stores information needed to manage it, such as the file name, its storage location, the matter it is attached to and who uploaded it.",
                  "When you delete a document in the application, it is marked as deleted and is no longer available through the application.",
                ]}
              />
            </Section>

            <Section id="portal" title="5. Client portal and invitations">
              <p>
                A firm can invite its client to a read-only client portal. When a firm sends an invitation, the
                application sends an email to the address the firm provides and stores an invitation record so the
                invitation can be accepted and can expire.
              </p>
              <Bullets
                items={[
                  "The client portal is read-only. A client signing in can see only their own matter and the updates the firm chooses to publish.",
                  "A client cannot see the firm's internal notes, other clients, other matters or firm-wide data.",
                  "The firm controls whether to invite a client and what to publish to the portal.",
                ]}
              />
            </Section>

            <Section id="notifications" title="6. Notifications and communication">
              <p>
                The application creates in-app notifications about your own matters, such as upcoming hearings and
                tasks, and stores the notification preferences you set so it can respect your choices.
              </p>
              <p>
                Transactional emails are sent where they are needed to operate the service — for example password
                reset emails, client portal invitations and payment receipts. These emails are sent through the
                email delivery service the application is configured to use. Advocate Diary does not send
                marketing or promotional email through the application.
              </p>
            </Section>

            <Section id="payments" title="7. Payment and subscription information">
              <p>
                Subscriptions are processed through <strong className="text-navy">Razorpay</strong>, a payment
                gateway. When you subscribe, you are taken to Razorpay's checkout to complete the payment.
              </p>
              <Bullets
                items={[
                  "Advocate Diary does not store your card number, CVV, UPI PIN, net banking credentials or any other payment instrument credentials. These are entered into and handled by Razorpay, not by Advocate Diary.",
                  "The application stores subscription information needed to run your account, such as the plan you are on, the status of the subscription, trial and renewal information, and whether it has been cancelled.",
                  "The application stores payment records returned by the gateway, such as the payment status, amount and the date a payment was made, together with the gateway's own reference identifiers, so that your plan and receipts are correct.",
                  "Razorpay's handling of your payment details is governed by Razorpay's own terms and privacy policy.",
                ]}
              />
            </Section>

            <Section id="security-info" title="8. Password and security information">
              <p>
                Passwords are not stored in a readable form. When you set a password it is converted into a
                one-way hash using bcrypt, and only that hash is stored. Nobody operating the service can read
                your password back, which is also why a forgotten password must be reset rather than retrieved.
              </p>
              <Bullets
                items={[
                  "Passwords must be at least eight characters.",
                  "Password reset links are stored only as hashes, are single use and expire.",
                  "Repeated failed sign-in attempts are rate limited to make guessing attacks harder.",
                  "Signing out invalidates the session token so it cannot be reused.",
                ]}
              />
            </Section>

            <Section id="use" title="9. How we use information">
              <p>We use the information described above to:</p>
              <Bullets
                items={[
                  "Provide the service — maintain your account, your firm's records, your diary, documents and reports.",
                  "Authenticate you, keep you signed in, and apply the role permitted to your account.",
                  "Send transactional messages needed to operate the service, such as password resets, client portal invitations and payment receipts.",
                  "Manage subscriptions, plan limits, trials and billing status.",
                  "Keep the service secure — including rate limiting sign-in attempts and authorising each document download.",
                  "Operate the service overall, including understanding aggregate usage such as the number of accounts and subscriptions, so the service can be maintained and supported.",
                ]}
              />
              <p>
                We do not sell your information, and we do not use the contents of your matters, clients or
                documents for advertising.
              </p>
            </Section>

            <Section id="isolation" title="10. Organisation-level access isolation">
              <p>
                Advocate Diary is built so that each organisation's records are scoped to that organisation. Every
                record you create belongs to your organisation, and requests are answered only from within it,
                so one firm's data is not visible to another firm's users.
              </p>
              <Bullets
                items={[
                  "Within a firm, what a member can do depends on the role assigned to them — for example only a firm administrator can manage the team.",
                  "Client portal users are limited to their own matter and cannot reach firm data.",
                  "Platform administration, used to operate the service itself, is separate from firm accounts. A platform administrator account does not belong to any organisation and is blocked from firm and organisation records; equally, firm users cannot reach platform administration.",
                ]}
              />
            </Section>

            <Section id="third-parties" title="11. Third-party services we use">
              <p>
                The application uses the following third-party services, and only for the purposes described:
              </p>
              <Bullets
                items={[
                  "Razorpay — to process subscription payments and to confirm payment events.",
                  "Cloudflare R2 — to store the documents you upload.",
                  "An email delivery service — to send transactional email such as password resets, client portal invitations and payment receipts.",
                  "Google sign-in — used only where you choose to sign in with Google, to confirm your identity for sign-in.",
                  "Cloud hosting and a managed database — used to run the application and store your records.",
                ]}
              />
              <p>
                These services receive only what they need to perform that function. Advocate Diary does not use
                advertising networks or analytics trackers in the application.
              </p>
            </Section>

            <Section id="your-security" title="12. Your account security responsibilities">
              <p>You are responsible for the security of your own account. In particular:</p>
              <Bullets
                items={[
                  "Keep your password confidential and do not share your sign-in credentials.",
                  "Use a password that you do not reuse on other services, and change it if you believe it has been exposed.",
                  "Sign out on devices that are not yours, including shared or court-room devices.",
                  "Add team members only where they should have access to your firm's records, give each the correct role, and remove access when a member leaves.",
                  "Invite a client to the portal only at the email address you have verified with that client.",
                  "Tell us promptly if you believe an account has been accessed without authorisation.",
                ]}
              />
            </Section>

            <Section id="deletion" title="13. Data deletion and account closure">
              <p>
                You can edit or delete records from within the application at any time. Deleting a record removes
                it from the application for your users.
              </p>
              <p>
                You can cancel a paid subscription from Settings. Cancelling a subscription changes your billing
                and plan; it is not by itself a request to delete your account.
              </p>
              <p>
                If you want your account and your firm's data removed, you can ask us using the contact details
                in the next section. We will act on that request. We do not state a fixed retention or deletion
                period here, because no specific retention period has been established for the service — so
                rather than promise a period we do not operate to, we will confirm to you what has been done
                when we handle your request. Where information must be kept in order to meet a legal or
                accounting obligation, such as records of payments already made, it will be retained for that
                purpose only.
              </p>
            </Section>

            <Section id="requests" title="14. Privacy requests and contact">
              <p>
                For any question about this policy, or to make a request about your information — including access
                to it, correction of it, or deletion of your account — contact us at:
              </p>
              <p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="policy-contact-email"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p>
                Please write from the email address registered on your account, so that we can be reasonably
                satisfied the request comes from you before acting on it. We may ask you for further information
                to confirm your identity where a request concerns account data.
              </p>
              <p>
                If you are a client of a firm that uses Advocate Diary and your request concerns the information
                that firm holds about your matter, please contact the firm directly. The firm decides what is
                recorded about its clients and matters.
              </p>
              <p>
                No telephone number or postal address is published for privacy requests, and none should be
                assumed. Email is the contact route for the service.
              </p>
            </Section>

            <Section id="updates" title="15. Changes to this policy">
              <p>
                We may update this policy as the application changes. The effective date at the top of this page
                shows when the current version took effect. Where a change materially affects how information is
                handled, we will make the updated policy available on this page before continuing to operate
                under it. Continuing to use the service after an update means the updated policy applies to your
                use of it.
              </p>
            </Section>

            <Section id="law" title="16. Governing law">
              <p>
                This policy and the handling of information described in it are governed by the laws of India,
                including the Digital Personal Data Protection Act, 2023 and the Information Technology Act,
                2000, as applicable. No particular court or place of jurisdiction is specified in this policy.
              </p>
            </Section>

            <div className="rounded-lg border border-border bg-secondary/60 p-5">
              <p className="text-xs leading-relaxed text-muted-foreground">
                This page describes the privacy practices of the Advocate Diary application as currently
                operated. It intentionally does not state a legal entity name, registered address, telephone
                number, certification, retention period or use of cookies, analytics or advertising technology,
                because none of those has been established or configured for the service. This page will be
                updated if that changes. Please also read our{" "}
                <Link
                  to="/terms"
                  data-testid="policy-footer-terms"
                  className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  Terms of Service
                </Link>
                , which apply when you use the service.
              </p>
            </div>
          </article>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
